import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete, AutocompleteOption, Button, Modal, Select, Tooltip } from 'shared-ui';

import { PrzejscieModDescriptionEnum, PrzejscieModEnum } from 'api/strefy/strefy.enum';
import { transformTreeToFlatArray } from 'helpers/transformTreeToFlatArray';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { Form } from 'ui/form/Form';
import { TreeSingleSelect } from 'ui/organizationsTreeSelect/treeSingleSelect/TreeSingleSelect';

import { AddPassageFormBody, AddPassageProps } from './AddPassage.types';
import * as Styled from './AddPassage.styles';

export const AddPassage = ({
  servers,
  zones,
  readers,
  isLoadingReaders,
  isAllReadersDefined,
  isDisabled,
  disabledReason,
  isLoading,
  onSubmit,
  isModalOpen,
  onModalOpen,
  onModalClose,
}: AddPassageProps) => {
  const { formatMessage } = useLocale();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { isValid },
  } = useFormContext<AddPassageFormBody>();

  const [rcpId, tkdId, strefaKoncowaId] = watch(['rcpId', 'tkdId', 'strefaKoncowaId']);
  const flatZones = transformTreeToFlatArray(zones);
  const selectedReader = readers.find(({ id }) => id === tkdId);
  const allReadersDefinedMessage = isAllReadersDefined
    ? formatMessage({ id: AppMessages['definitions.passages.add.reader.allDefined'] })
    : undefined;

  const serverOptions = servers.map(({ rcpId, nazwaRcp }) => ({ value: rcpId, label: nazwaRcp }));
  const readerOptions = readers.map(({ id, nazwa, numerLogicznyKontrolera }) => ({
    value: id,
    label: nazwa,
    number: numerLogicznyKontrolera,
  }));
  // Only the mods the reader has no przejście for yet (`dostepneMody`, contract §2).
  const modOptions = (selectedReader?.dostepneMody || []).map((mod: PrzejscieModEnum) => ({
    value: mod,
    label: formatMessage({ id: PrzejscieModDescriptionEnum[mod] }),
  }));

  const onServerChange = (serverId: string) => {
    setValue('rcpId', serverId, { shouldValidate: true });
    setValue('tkdId', '');
    setValue('mod', '');
  };

  const onReaderChange = (readerId: string) => {
    const reader = readers.find(({ id }) => id === readerId);
    const [onlyMod] = reader?.dostepneMody.length === 1 ? reader.dostepneMody : [];

    setValue('tkdId', readerId, { shouldValidate: true });
    setValue('mod', onlyMod || '', { shouldValidate: !!onlyMod });
  };

  const renderZoneSelect = (name: 'strefaPoczatkowaId' | 'strefaKoncowaId', label: string) => (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Styled.ZoneField $startsRow={name === 'strefaPoczatkowaId'}>
          <TreeSingleSelect
            options={zones}
            value={flatZones.find(({ id }) => id === field.value)}
            onChange={(zone) => {
              field.onChange(zone?.id || '');

              if (name === 'strefaPoczatkowaId' && strefaKoncowaId) {
                trigger('strefaKoncowaId');
              }
            }}
            label={label}
            placeholder={formatMessage({ id: AppMessages['definitions.passages.add.zone.placeholder'] })}
            required
          />
          <Styled.ErrorMessage>{error?.message}</Styled.ErrorMessage>
        </Styled.ZoneField>
      )}
    />
  );

  return (
    <>
      <Tooltip title={isDisabled && disabledReason ? disabledReason : ''}>
        <Styled.ButtonWrapper>
          <Button data-testid="add-passage" variant="contained" disabled={isDisabled} onClick={onModalOpen}>
            {formatMessage({ id: AppMessages['definitions.passages.add.title'] })}
          </Button>
        </Styled.ButtonWrapper>
      </Tooltip>

      <Modal
        header={formatMessage({ id: AppMessages['definitions.passages.add.title'] })}
        open={isModalOpen}
        onClose={onModalClose}
        type="form"
        onSubmit={handleSubmit(onSubmit)}
        submitButtonDisabled={!isValid || isLoading}
      >
        <Form.Grid>
          <Controller
            name="rcpId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                onChange={(e) => onServerChange(e.target.value as string)}
                label={formatMessage({ id: AppMessages['definitions.passages.add.server.label'] })}
                options={serverOptions}
                errorMessage={error?.message}
                withHelperText
                required
              />
            )}
          />

          <Controller
            name="tkdId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Autocomplete
                  data-testid="passage-reader"
                  onBlur={field.onBlur}
                  onChange={(_, option) => onReaderChange(option ? `${option.value}` : '')}
                  value={readerOptions.find(({ value }) => value === field.value) || null}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  options={readerOptions}
                  renderOption={(props, option) => (
                    <AutocompleteOption
                      key={option.value}
                      props={props}
                      id={option.number || ''}
                      label={option.label}
                    />
                  )}
                  disabled={!rcpId || isAllReadersDefined}
                  loading={isLoadingReaders}
                  loadingText={formatMessage({ id: AppMessages['common.loading'] })}
                  noOptionsText={formatMessage({ id: AppMessages['common.notFound'] })}
                  label={formatMessage({ id: AppMessages['definitions.passages.add.reader.label'] })}
                  placeholder={formatMessage({ id: AppMessages['definitions.passages.add.reader.placeholder'] })}
                  required
                />
                <Styled.ErrorMessage>{allReadersDefinedMessage || error?.message}</Styled.ErrorMessage>
              </div>
            )}
          />

          <Controller
            name="mod"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                disabled={!selectedReader}
                label={formatMessage({ id: AppMessages['definitions.passages.add.mod.label'] })}
                placeholder={formatMessage({ id: AppMessages['definitions.passages.add.mod.placeholder'] })}
                options={modOptions}
                errorMessage={allReadersDefinedMessage || error?.message}
                withHelperText
                required
              />
            )}
          />

          {renderZoneSelect(
            'strefaPoczatkowaId',
            formatMessage({ id: AppMessages['definitions.passages.add.startZone.label'] }),
          )}

          {renderZoneSelect(
            'strefaKoncowaId',
            formatMessage({ id: AppMessages['definitions.passages.add.endZone.label'] }),
          )}
        </Form.Grid>
      </Modal>
    </>
  );
};
